import { useState, useEffect } from 'react';
import { Store } from 'lucide-react';
import { isCapacitorNative } from '@/lib/platformDetect';
import { getPlatformBranding, platformLogoSrc, PLATFORM_FALLBACK } from '@/lib/platformBranding';

// Shared, presentational pre-auth brand header for the Cashier + Waiter PIN
// screens. Reads the central SmartoERP PLATFORM branding (super-admin
// controlled) — NOT tenant branding — so the app identity is consistent and
// no company logo leaks pre-auth. Purely visual; holds NO PIN / auth logic. A
// branding or version failure NEVER blocks login.

export function PosLoginBrand({ role, icon: Icon = Store, showVersion = false }) {
  const [brand, setBrand] = useState(PLATFORM_FALLBACK);
  const [logoBroken, setLogoBroken] = useState(false);
  const [version, setVersion] = useState('');

  useEffect(() => {
    let alive = true;
    getPlatformBranding().then((b) => { if (alive && b) setBrand(b); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!showVersion || !isCapacitorNative()) return undefined;
    let alive = true;
    import('@capacitor/app')
      .then((m) => m.App.getInfo())
      .then((info) => {
        if (alive && info?.version) {
          setVersion(`v${info.version}${info.build ? ` (Build ${info.build})` : ''}`);
        }
      })
      .catch(() => { /* native version optional — hide silently */ });
    return () => { alive = false; };
  }, [showVersion]);

  const logo = platformLogoSrc(brand);
  const showImg = logo && !logoBroken;

  return (
    <div className="text-center" data-testid="pos-login-brand">
      <div className="mx-auto w-16 h-16 landscape:w-12 landscape:h-12 rounded-2xl bg-orange-100 flex items-center justify-center mb-4 landscape:mb-2 overflow-hidden">
        {showImg ? (
          <img
            src={logo}
            alt={brand.app_name}
            className="w-full h-full object-contain"
            onError={() => setLogoBroken(true)}
            data-testid="pos-login-logo"
          />
        ) : (
          <Icon size={32} className="text-orange-600" data-testid="pos-login-icon" />
        )}
      </div>
      <h1 className="text-2xl font-bold font-outfit text-foreground" data-testid="pos-login-appname">
        {brand.app_name || 'SmartoERP'}
      </h1>
      <p className="text-sm font-semibold text-orange-600 mt-0.5" data-testid="pos-login-role">{role}</p>
      {showVersion && version && (
        <p className="text-[11px] text-muted-foreground mt-1 font-mono" data-testid="pos-login-version">
          SmartoERP POS {version}
        </p>
      )}
    </div>
  );
}
